import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Hero from "src/components/Hero/Hero";
import EventFinished from "src/components/EventFinished";
import TitleSection from "src/components/TitleSection/TitleSection";

import { COMPONENT_MAP, TitanSubtitle, TitanTime, TitanPromoCallout } from "./titanTuesday";
import {
  buildLegacySections,
  normalizeSections,
  resolveHeroImage,
  resolvePageConfig,
} from "./TitanTuesdayBuilder.utils";

const TitanTuesdayBuilder = ({
  contentKey,
  sections: sectionsProp,
  scheduleKeyName,
  heroBgImage,
  heroImage,
  heroImageAlt,
  highlightColor,
  includeIntro2,
  joinUsDateKey,
  payoutIsMarkdown,
  bonusTextColor,
  heroTitleFullWidth,
}) => {
  const { t, i18n } = useTranslation();
  const content = t(`poker.${contentKey}`, { returnObjects: true });

  const config = resolvePageConfig(content, {
    scheduleKeyName: scheduleKeyName ?? contentKey,
    heroBgImage,
    heroImage,
    heroImageAlt,
    highlightColor,
    includeIntro2,
    joinUsDateKey,
    payoutIsMarkdown,
    bonusTextColor,
    heroTitleFullWidth,
  });

  const rawSections =
    sectionsProp ||
    content?.sections ||
    buildLegacySections({ content, includeIntro2: config.includeIntro2 });
  const sections = normalizeSections(rawSections);
  const joinUsSuffix = config.joinUsDateKey ? content?.[config.joinUsDateKey] : "";

  const renderSection = (section, index) => {
    const { type, props = {}, container = "div" } = section || {};
    const Component = COMPONENT_MAP[type];

    if (!Component) {
      console.warn(`Titan Tuesday section type "${type}" not found in registry`);
      return null;
    }

    const el = <Component {...props} highlightColor={config.highlightColor} />;

    if (container === "fluid") {
      return <React.Fragment key={`section-${type}-${index}`}>{el}</React.Fragment>;
    }

    const ContainerTag = container === "wide" ? "container-wide" : "container-div";
    return <ContainerTag key={`section-${type}-${index}`}>{el}</ContainerTag>;
  };

  return (
    <PageDiv>
      <EventFinished scheduleKeyName={config.scheduleKeyName} eventType="promotion" />
      <Hero
        bgImage={config.heroBgImage}
        image={resolveHeroImage(config.heroImage, i18n.language)}
        imageAlt={config.heroImageAlt}
      />

      <container-wide>
        <TitleSection
          title={content?.title}
          className="pt-0 pb-0"
          borderColor={config.highlightColor}
        />
      </container-wide>

      <container-div>
        <TitanSubtitle content={content?.subtitle} />
        <TitanTime time={content?.time} />
      </container-div>

      <container-div>
        <TitanPromoCallout
          payout={content?.payout}
          payoutIsMarkdown={config.payoutIsMarkdown}
          joinUs={content?.joinUs}
          joinUsSuffix={joinUsSuffix}
          highlightColor={config.highlightColor}
          bonusTextColor={config.bonusTextColor}
        />
      </container-div>

      {sections.map(renderSection)}
    </PageDiv>
  );
};

const PageDiv = styled.div`
  width: 100%;
  position: relative;
  background-color: #010101;
`;

export default TitanTuesdayBuilder;
